import axios from 'axios';
import { RequestError } from '@ers/errors';
import { history } from '@ers/helpers';
import { getJson, isStatusOK, isValidStatus } from '@ers/helpers/api/ResponseProcessors';
import { getAccessToken } from '@ers/root/App.selectors';
import store from '@ers/root/App.store';
import { formatErrorMessage } from '../Errors';

class BaseClient {
    constructor(rootUrl) {
        this.rootUrl = rootUrl;
        axios.defaults.headers.get.Pragma = 'no-cache';
        axios.defaults.headers.get['Cache-Control'] = 'no-cache, no-store';
        axios.defaults.validateStatus = isValidStatus();
    }

    format(url) {
        return `${this.rootUrl}${url}`;
    }

    prepareHeaders(headerFuncs) {
        const headers = {};
        headerFuncs.forEach((fn) => {
            const { key, value } = fn();
            headers[key] = value;
        });
        return { headers };
    }

    validateResponse(data) {
        return data;
    }

    prepareAuth() {
        const { getState } = store;
        const token = getAccessToken(getState());
        return { key: 'Authorization', value: `Bearer ${token}` };
    }


    get(url, postprocess = getJson) {
        const config = this.prepareHeaders([this.prepareAuth]);
        const formattedUrl = this.format(url);
        return axios
            .get(formattedUrl, config)
            .then(this.validateResponse)
            .then(postprocess)
            .catch(createError(formattedUrl));
    }

    fetchFile(url, postprocess = getJson) {
        const config = { ...this.prepareHeaders([this.prepareAuth]), responseType: 'blob' };
        const formattedUrl = this.format(url);
        return axios
            .get(formattedUrl, config)
            .then(this.validateResponse)
            .then(postprocess)
            .catch(createError(formattedUrl));
    }

    patch(url, body, postprocess = isStatusOK, validStatuses) {
        const formattedUrl = this.format(url);
        const config = this.prepareHeaders([this.prepareAuth]);
        if (validStatuses) {
            config.validateStatus = isValidStatus(validStatuses);
        }
        return axios
            .patch(formattedUrl, body, config)
            .then(this.validateResponse)
            .then(postprocess)
            .catch(createError(formattedUrl));
    }

    delete(url, postprocess = isStatusOK, validStatuses) {
        const formattedUrl = this.format(url);
        const config = this.prepareHeaders([this.prepareAuth]);
        if (validStatuses) {
            config.validateStatus = isValidStatus(validStatuses);
        }
        return axios
            .delete(formattedUrl, config)
            .then(this.validateResponse)
            .then(postprocess)
            .catch(createError(formattedUrl));
    }

    post(url, body, postprocess = isStatusOK, validStatuses) {
        const formattedUrl = this.format(url);
        const config = this.prepareHeaders([this.prepareAuth]);
        if (validStatuses) {
            config.validateStatus = isValidStatus(validStatuses);
        }
        return axios
            .post(formattedUrl, body, config)
            .then(this.validateResponse)
            .then(postprocess)
            .catch(createError(formattedUrl));
    }

    head(url, postprocess = isStatusOK) {
        const formattedUrl = this.format(url);
        const config = this.prepareHeaders([this.prepareAuth]);
        return axios
            .head(formattedUrl, config)
            .then(this.validateResponse)
            .then(postprocess)
            .catch(createError(formattedUrl));
    }
}

export default BaseClient;

const createError = (url) => (e) => {
    if (e.isAxiosError) {
        return new RequestError(e, url);
    }
    history.goToError();
    // eslint-disable-next-line no-console
    console.error(...formatErrorMessage(e.message));
    return new Error(e);
};